class StrongSocket {
    constructor(url, updateStatus) {
        this.options = {
            idle: false,
            pongTimeout: 9000,
            autoReconnectDelay: 3500,
            protocol: location.protocol === 'https:' ? 'wss:' : 'ws:',
            pingDelay: 2500,
        };
        this.url = url;
        this.averageLag = 0;
        this.currentLag = 0;
        this.lastPingTime = performance.now();
        this.pongCount = 0;
        this.heartbeat = browserTaskQueueMonitor(1000);
        this.updateStatus = updateStatus;
        this.connect();
    }
    connect() {
        this.destroy();
        if (!this.isOnline()) {
            document.body.classList.remove('online');
            document.body.classList.add('offline');
            this.updateStatus('Offline');
            this.scheduleConnect(4000);
            return;
        }
        try {
            this.ws = new WebSocket(this.url);
            this.ws.onerror = (e) => this.onError(e);
            this.ws.onclose = this.onClose.bind(this);
            this.ws.onopen = () => {
                document.body.classList.remove('offline');
                document.body.classList.add('online');
                this.onSuccess();
                this.pingNow();
            };
            this.ws.onmessage = (e) => {
                // console.log('Received:', e.data);
                if (e.data === 'PONG')
                    return this.pong();
                this.handle(e.data);
            };
        }
        catch (e) {
            this.onClose();
        }
        this.scheduleConnect();
    }
    scheduleConnect(delay = this.options.pongTimeout) {
        if (this.options.idle)
            delay = 10 * 1000 + Math.random() * 10 * 1000;
        clearTimeout(this.pingSchedule);
        clearTimeout(this.connectSchedule);
        this.connectSchedule = setTimeout(() => {
            document.body.classList.add('offline');
            document.body.classList.remove('online');
            this.connect();
        }, delay);
    }
    schedulePing(delay) {
        clearTimeout(this.pingSchedule);
        this.pingSchedule = setTimeout(() => this.pingNow(), delay);
    }
    pingNow() {
        var _a;
        // console.log('PingNow');
        clearTimeout(this.pingSchedule);
        clearTimeout(this.connectSchedule);
        try {
            const data = { t: 'p', l: this.averageLag.toString() };
            (_a = this.ws) === null || _a === void 0 ? void 0 : _a.send(JSON.stringify(data));
            this.lastPingTime = performance.now();
        }
        catch (e) {
            console.error(e);
        }
        this.scheduleConnect();
    }
    computePingDelay() {
        return this.options.pingDelay + (this.options.idle ? 1000 : 0);
    }
    pong() {
        // console.log('Pong');
        clearTimeout(this.connectSchedule);
        this.schedulePing(this.computePingDelay());
        const currentLag = Math.min(performance.now() - this.lastPingTime, 10000);
        this.pongCount++;
        const mix = this.pongCount > 4 ? 0.1 : 1 / this.pongCount;
        this.averageLag += mix * (currentLag - this.averageLag);
        this.currentLag = currentLag;
    }
    destroy() {
        clearTimeout(this.pingSchedule);
        clearTimeout(this.connectSchedule);
        this.disconnect();
        this.ws = undefined;
    }
    disconnect() {
        const ws = this.ws;
        if (ws) {
            ws.onerror = ws.onclose = ws.onopen = ws.onmessage = () => { };
            ws.close();
        }
    }
    onError(e) {
        if (this.heartbeat.wasSuspended)
            return;
        console.error('WebSocket error:', e);
    }
    onClose() {
        if (this.heartbeat.wasSuspended)
            return this.onSuspended();
        if (this.ws) {
            this.scheduleConnect(this.options.autoReconnectDelay);
        }
    }
    onSuccess() {
        this.updateStatus('Online');
        idleTimer(5 * 1000, () => {
            // console.log('Idle');
            this.options.idle = true;
        }, () => {
            // console.log('Active');
            this.options.idle = false;
            if (!this.ws)
                location.reload();
        });
    }
    isOnline() {
        return !('onLine' in navigator) || navigator.onLine;
    }
    handle(msg) { }
    getAverageLag() {
        return this.averageLag;
    }
    getCurrentLag() {
        return this.currentLag;
    }
    onSuspended() {
        this.heartbeat.reset();
        clearTimeout(this.pingSchedule);
        clearTimeout(this.connectSchedule);
        this.connect();
    }
}
function idleTimer(delay, onIdle, onWakeUp) {
    const events = ['mousemove', 'touchstart'];
    let listening = false;
    let active = true;
    let lastSeenActive = performance.now();
    const onActivity = () => {
        if (!active) {
            onWakeUp();
        }
        active = true;
        lastSeenActive = performance.now();
        stopListening();
    };
    const startListening = () => {
        if (!listening) {
            events.forEach((e) => document.addEventListener(e, onActivity));
            listening = true;
        }
    };
    const stopListening = () => {
        if (listening) {
            events.forEach((e) => document.removeEventListener(e, onActivity));
            listening = false;
        }
    };
    setInterval(() => {
        if (active && performance.now() - lastSeenActive > delay) {
            // console.log('Idle');
            onIdle();
            active = false;
        }
        startListening();
    }, 1000);
}
function browserTaskQueueMonitor(interval = 1000) {
    let lastTime;
    let timeout;
    let suspended = false;
    start();
    return {
        get wasSuspended() {
            return suspended;
        },
        reset() {
            suspended = false;
            clearTimeout(timeout);
            start();
        },
    };
    function monitor() {
        if (performance.now() - lastTime > interval + 400)
            suspended = true;
        else
            start();
    }
    function start() {
        lastTime = performance.now();
        timeout = setTimeout(monitor, interval);
    }
}
export function measureLatency() {
    const socket = new StrongSocket('wss://api.staging.zephyr.pressone.co:443', (newStatus) => {
        console.log('Connection status:', newStatus);
    });
    return {
        socket,
        getAverageLag() {
            return socket.getAverageLag();
        },
        getCurrentLag() {
            return socket.getCurrentLag();
        },
    };
}
